<template>
  <div>
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运营部</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/yunying/choujian' }">筹建列表</el-breadcrumb-item>
      <el-breadcrumb-item>筹建详情</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 页面内容 -->
    <el-card class="box-card" style="margin-top: 20px;">
      <el-page-header @back="goBack" content="筹建详情">
      </el-page-header>

      <el-divider></el-divider>

      <!-- 加载状态 -->
      <div v-if="pageLoading" v-loading="true" style="min-height: 400px;"></div>

      <!-- 详情内容（只读：运营视角无任何操作按钮） -->
      <div v-else-if="currentDetail" class="detail-page-container">
        <el-row :gutter="20">
          <el-col :span="12" :offset="0">
            <!-- ===== 顶部项目信息区 ===== -->
            <div class="detail-header">
              <div class="detail-header-left">
                <div class="detail-project-name">
                  <i class="el-icon-s-shop"></i>
                  {{ currentDetail.instance.projectName || '-' }}
                  <el-tooltip content="刷新详情" placement="top">
                    <i class="el-icon-refresh detail-refresh-icon" @click="refreshDetail"></i>
                  </el-tooltip>
                </div>
                <div class="detail-meta-row">
                  <span class="detail-meta-item">
                    <span class="detail-meta-label">筹建督导：</span>
                    <span class="detail-meta-value">{{ getAssigneeName(currentDetail.instance.assignees, 'supervisor') }}</span>
                  </span>
                  <span class="detail-meta-item">
                    <span class="detail-meta-label">接收时间：</span>
                    <span class="detail-meta-value">{{ currentDetail.instance.startedAt | date }}</span>
                  </span>
                </div>
                <div class="detail-address" v-if="detailAddress">
                  <i class="el-icon-location-outline"></i>
                  <span class="detail-address-text">{{ detailAddress }}</span>
                </div>
              </div>
              <div class="detail-progress-card">
                <div class="detail-progress-label">整体进度</div>
                <div class="detail-progress-value">
                  <span class="detail-progress-number">{{ currentDetail.instance.progress?.percentage || 0 }}</span>
                  <span class="detail-progress-unit">%</span>
                </div>
                <el-progress
                  :percentage="currentDetail.instance.progress?.percentage || 0"
                  :color="getProgressColor(currentDetail.instance.progress?.percentage)"
                  :stroke-width="8"
                  :show-text="false">
                </el-progress>
                <div class="detail-progress-info">
                  {{ currentDetail.instance.progress?.completed || 0 }}/{{ currentDetail.instance.progress?.total || 0 }} 节点完成
                </div>
              </div>
            </div>

            <!-- 首批物料备忘（招商部录入，详情接口自动下发；与督导详情页同款） -->
            <div class="detail-memo-card" v-if="hasMemo(projectMemo)">
              <div class="detail-memo-header">
                <span class="detail-memo-title"><i class="el-icon-warning-outline"></i> 首批物料备忘</span>
                <span class="detail-memo-source">招商部提供</span>
              </div>
              <div class="detail-memo-text" v-if="projectMemo.text">{{ projectMemo.text }}</div>
              <div class="detail-memo-images" v-if="projectMemo.images && projectMemo.images.length">
                <el-image
                  v-for="(img, idx) in projectMemo.images"
                  :key="idx"
                  :src="getMemoImg(img)"
                  :preview-src-list="projectMemo.images.map(i => getMemoImg(i))"
                  style="width: 40px; height: 40px; margin-right: 6px; cursor: pointer; border-radius: 4px;"
                  fit="cover">
                </el-image>
              </div>
            </div>

            <!-- ===== 阶段进度区域 ===== -->
            <div class="detail-section">
              <div class="detail-section-title">阶段进度</div>
              <div class="stage-cards">
                <div
                  v-for="stage in stageList"
                  :key="stage.code"
                  class="stage-card"
                  :class="getStageClass(stage)">
                  <div class="stage-name">{{ stage.name }}</div>
                  <div class="stage-status">{{ getStageStatusText(stage) }}</div>
                  <el-progress
                    :percentage="getStagePercentage(stage)"
                    :color="getStageColor(stage)"
                    :stroke-width="4"
                    :show-text="false">
                  </el-progress>
                </div>
              </div>
            </div>

            <!-- ===== 资料收集区域 ===== -->
            <div class="detail-section" v-if="dataCollectNode && dataCollectNode.status === 'COMPLETED'">
              <div class="detail-section-header">
                <span class="detail-section-title">资料收集</span>
              </div>
              <el-descriptions :column="3" size="small" border>
                <el-descriptions-item label="完成时间">{{ dataCollectNode.completedAt | date }}</el-descriptions-item>
                <el-descriptions-item label="门店地址" :span="2">{{ dataCollectNode.data?.storeAddress || '-' }}</el-descriptions-item>
                <el-descriptions-item label="法人姓名">{{ dataCollectNode.data?.legalPersonName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="法人身份证">{{ dataCollectNode.data?.legalPersonId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="店铺绑定号码">{{ dataCollectNode.data?.storePhone || '-' }}</el-descriptions-item>
                <el-descriptions-item label="开户行">{{ dataCollectNode.data?.bankName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="银行卡号">{{ dataCollectNode.data?.bankAccount || '-' }}</el-descriptions-item>
                <el-descriptions-item label="银行预留号码">{{ dataCollectNode.data?.bankPhone || '-' }}</el-descriptions-item>
                <el-descriptions-item label="备注">{{ dataCollectNode.data?.remark || '暂无' }}</el-descriptions-item>
                <el-descriptions-item label="图片" :span="2">
                  <el-image
                    v-for="(img, idx) in (dataCollectNode.data?.images || [])"
                    :key="idx"
                    :src="base + img"
                    :preview-src-list="(dataCollectNode.data?.images || []).map(i => base + i)"
                    style="width: 30px; height: 30px; margin-right: 5px; cursor: pointer;"
                    fit="cover">
                  </el-image>
                  <span v-if="!dataCollectNode.data?.images?.length">-</span>
                </el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- ===== 平台账号区域 ===== -->
            <div class="detail-section" v-if="accountNodes.create && accountNodes.create.status === 'COMPLETED'">
              <div class="detail-section-title">平台账号</div>
              <el-descriptions :column="3" size="small" border>
                <el-descriptions-item label="建店专员">{{ accountNodes.assign?.data?.employeeName || accountNodes.create?.assigneeName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="通知时间">{{ accountNodes.notify?.completedAt | date }}</el-descriptions-item>
                <el-descriptions-item label="创建完成">{{ accountNodes.create?.completedAt | date }}</el-descriptions-item>
                <el-descriptions-item label="美团店铺名称">{{ accountNodes.create?.data?.meituanName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="美团店铺ID">{{ accountNodes.create?.data?.meituanId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="">-</el-descriptions-item>
                <el-descriptions-item label="淘宝店铺名称">{{ accountNodes.create?.data?.taobaoName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="淘宝店铺ID">{{ accountNodes.create?.data?.taobaoId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="">-</el-descriptions-item>
                <el-descriptions-item label="京东店铺名称">{{ accountNodes.create?.data?.jdName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="京东店铺ID">{{ accountNodes.create?.data?.jdId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="">-</el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- ===== 产品审核区域 ===== -->
            <div class="detail-section" v-if="productReviewNode && productReviewNode.loopRecords && productReviewNode.loopRecords.length > 0">
              <div class="detail-section-title">产品审核</div>
              <el-table :data="productReviewNode.loopRecords" border size="small">
                <el-table-column type="index" label="序号" width="60"></el-table-column>
                <el-table-column label="审核时间" width="180">
                  <template slot-scope="scope">{{ scope.row.date | date }}</template>
                </el-table-column>
                <el-table-column label="图片" min-width="200">
                  <template slot-scope="scope">
                    <span v-if="scope.row.images && scope.row.images.length > 0">
                      <el-image
                        v-for="(img, idx) in scope.row.images"
                        :key="idx"
                        :src="base + img"
                        :preview-src-list="scope.row.images.map(i => base + i)"
                        style="width: 30px; height: 30px; margin-right: 5px; cursor: pointer;"
                        fit="cover">
                      </el-image>
                    </span>
                    <span v-else>--</span>
                  </template>
                </el-table-column>
                <el-table-column label="备注" width="150">
                  <template slot-scope="scope">{{ scope.row.remark || '--' }}</template>
                </el-table-column>
              </el-table>
            </div>

          </el-col>

          <el-col :span="12" :offset="0">
            <!-- ===== 节点详情表格 ===== -->
            <div class="detail-section">
              <div class="detail-section-header">
                <span class="detail-section-title">节点详情</span>
                <div class="detail-section-toggle">
                  <el-switch
                    v-model="showCompletedNodes"
                    active-text="显示已完成"
                    inactive-text="仅待处理"
                    size="small">
                  </el-switch>
                </div>
              </div>
              <el-table
                :data="filteredNodes"
                size="small"
                border
                stripe
                @row-click="handleNodeRowClick">
                <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
                <el-table-column label="节点名称" min-width="150">
                  <template slot-scope="scope">
                    {{ scope.row.name }}
                    <el-tag
                      v-if="scope.row.data && scope.row.data.transferReopen && scope.row.status !== 'COMPLETED'"
                      size="mini"
                      type="warning"
                      style="margin-left:5px;">
                      转让需重开
                    </el-tag>
                    <span v-if="scope.row.code === 'STORE_OPENING' && scope.row.status === 'COMPLETED' && scope.row.data?.openingAt && getOpenedDays(scope.row.data.openingAt) >= 0" class="node-days-tag">
                      （已开业{{ getOpenedDays(scope.row.data.openingAt) }}天）
                    </span>
                    <span v-if="scope.row.code === 'STORE_OPENING' && scope.row.status === 'COMPLETED' && scope.row.data?.openingAt && getOpenedDays(scope.row.data.openingAt) < 0" class="node-days-tag" style="color: #409EFF;">
                      （{{ -getOpenedDays(scope.row.data.openingAt) }}天后开业）
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="负责人" width="100">
                  <template slot-scope="scope">
                    {{ scope.row.assigneeName || '-' }}
                  </template>
                </el-table-column>
                <el-table-column label="状态" width="100" align="center">
                  <template slot-scope="scope">
                    <el-tag
                      :type="getNodeStatusType(scope.row.status)"
                      size="mini"
                      :effect="getNodeStatusType(scope.row.status)==='danger'?'dark':'light'">
                      {{ getNodeStatusText(scope.row) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="完成时间" width="110">
                  <template slot-scope="scope">
                    <span v-if="scope.row.status === 'COMPLETED' || scope.row.status === 'SKIPPED'">
                      {{ scope.row.completedAt | date }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="140" align="center">
                  <template slot-scope="scope">
                    <!-- 已完成且非决策节点：可查看详情 -->
                    <template v-if="scope.row.status === 'COMPLETED' && scope.row.type !== 'BRANCH'">
                      <el-button
                        type="text"
                        size="small"
                        @click.stop="handleNodeDetail(scope.row)">
                        查看
                      </el-button>
                    </template>
                    <!-- 其他情况：只读展示 -->
                    <span v-else-if="canExecuteNode(scope.row)" class="text-waiting">等待完成</span>
                    <span v-else class="text-muted">-</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-col>
        </el-row>

        <!-- ===== 底部信息区域（只读：仅展示当前节点与查看历史，无提交入口） ===== -->
        <div class="detail-actions" v-if="currentActiveNode">
          <div class="detail-actions-left">
            <span class="detail-actions-label">当前节点：</span>
            <span class="detail-actions-node">{{ currentActiveNode.name }}</span>
          </div>
          <div class="detail-actions-right">
            <el-button size="small" @click="showNodeHistory">查看历史</el-button>
          </div>
        </div>
      </div>

      <!-- 无数据提示 -->
      <el-empty v-else description="未找到筹建详情数据"></el-empty>
    </el-card>

    <!-- 节点详情弹窗（只读） -->
    <el-dialog
      title="节点详情"
      :visible.sync="nodeDetailDialogVisible"
      width="500px"
      :close-on-click-modal="false">
      <div v-if="currentDetailNode" class="node-detail-content">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="节点名称">{{ currentDetailNode.name }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getNodeStatusType(currentDetailNode.status)" size="small">
              {{ getNodeStatusText(currentDetailNode) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="负责人">{{ currentDetailNode.assigneeName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ currentDetailNode.completedAt | date }}</el-descriptions-item>
        </el-descriptions>

        <!-- 人员选择节点：显示被分配人信息 -->
        <div v-if="currentDetailNode.employeeSelection" class="node-detail-assignee">
          <div class="node-detail-section-title">分配信息</div>
          <el-descriptions :column="1" border size="small">
            <el-descriptions-item :label="currentDetailNode.employeeSelection.roleName">
              {{ currentDetailNode.data.employeeName || '-' }}
            </el-descriptions-item>
            <!-- 约定时间：仅当存在 scheduledAt 字段时显示 -->
            <el-descriptions-item v-if="currentDetailNode.data.scheduledAt" label="约定时间">
              {{ currentDetailNode.data.scheduledAt | date }}
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 动态显示 dataFields 中的数据（排除 employeeId、employeeName、scheduledAt） -->
        <div v-if="currentDetailNode.dataFields && currentDetailNode.dataFields.length > 0" class="node-detail-data">
          <template v-for="field in currentDetailNode.dataFields">
            <!-- 跳过隐藏字段、id字段、以及人员选择相关字段 -->
            <template v-if="!field.hidden && !field.fieldCode.endsWith('Id') && field.fieldCode !== 'employeeName' && field.fieldCode !== 'scheduledAt'">
              <div :key="field.fieldCode" class="node-detail-item">
                <div class="node-detail-label">{{ field.fieldName }}</div>
                <!-- 图片字段 -->
                <div v-if="field.fieldType === 'IMAGE'" class="node-detail-value">
                  <el-image
                    v-for="(img, idx) in (currentDetailNode.data[field.fieldCode] || [])"
                    :key="idx"
                    :src="base + img"
                    :preview-src-list="(currentDetailNode.data[field.fieldCode] || []).map(i => base + i)"
                    style="width: 60px; height: 60px; margin-right: 8px;"
                    fit="cover">
                  </el-image>
                  <span v-if="!currentDetailNode.data[field.fieldCode]?.length">-</span>
                </div>
                <!-- 日期字段 -->
                <div v-else-if="field.fieldType === 'DATE'" class="node-detail-value">
                  {{ currentDetailNode.data[field.fieldCode] | date }}
                </div>
                <!-- 其他字段（美团订货注册编码字段经 getNodeDataDisplayValue 取映射最新值） -->
                <div v-else class="node-detail-value">
                  {{ getNodeDataDisplayValue(field) || '-' }}
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="nodeDetailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 运营管理-筹建详情（只读）
// 从督导端 dudao/ChoujianDetail.vue 复制裁剪：删除全部节点执行/编辑/上传/地图能力，
// 详情数据复用通用接口 process/instances/:id，与督导工作台代码完全隔离。

// 首批物料备忘统一内容判空：兼容 undefined（历史文档）与空对象两种形态
const hasMemo = m => !!((m && m.text && m.text.trim()) || (m && m.images && m.images.length))

export default {
  name: 'YunyingChoujianDetail',
  data () {
    return {
      // 页面加载状态
      pageLoading: false,

      // 详情数据
      currentDetail: null,

      base: window.sessionStorage.getItem('base') || '',

      // 节点过滤
      showCompletedNodes: true,

      // 节点详情弹窗
      nodeDetailDialogVisible: false,
      currentDetailNode: null,

      // 阶段配置列表（根据流程模板定义）
      stageList: [
        { code: 'RECEIVE', name: '接收阶段', nodeCodes: ['RECEIVE', 'FIRST_COMMUNICATION'] },
        { code: 'DESIGN', name: '设计阶段', nodeCodes: ['DOOR_DESIGN_DECISION', 'DOOR_DESIGN_NOTIFY', 'DOOR_DESIGN_COMPLETE', 'DOOR_DESIGN_END', 'LAYOUT_DESIGN_DECISION', 'LAYOUT_DESIGN_NOTIFY', 'LAYOUT_DESIGN_COMPLETE', 'LAYOUT_DESIGN_REVIEW', 'LAYOUT_DESIGN_END'] },
        { code: 'PREPARE', name: '准备阶段', nodeCodes: ['COMPANY_TRAINING_BOOK', 'COMPANY_TRAINING_COMPLETE', 'EQUIPMENT_LIST', 'MATERIAL_LIST', 'MEITUAN_ORDER_REGISTER', 'FIRST_BATCH_REVIEW', 'BUSINESS_LICENSE', 'FOOD_LICENSE', 'CAMERA_SHARE', 'ONLINE_CHECK'] },
        { code: 'TRAINING', name: '培训操作阶段', nodeCodes: ['PRACTICAL_TRAINING_BOOK', 'PRACTICAL_TRAINING_COMPLETE', 'PRODUCT_AUDIT'] },
        { code: 'DATA_COLLECT', name: '资料收集', nodeCodes: ['DATA_COLLECT'] },
        { code: 'ACCOUNT', name: '账号阶段', nodeCodes: ['ACCOUNT_NOTIFY', 'ACCOUNT_CREATE', 'ASSIGN_OPERATOR'] },
        { code: 'AGREEMENT', name: '协议阶段', nodeCodes: ['MANAGEMENT_AGREEMENT', 'STORE_OPENING'] },
        { code: 'PRODUCT_REVIEW', name: '产品审核', nodeCodes: ['PRODUCT_REVIEW', 'FINISH_BUILD'] }
      ]
    }
  },
  computed: {
    // 项目首批物料备忘（详情接口 populate projectId 自动下发；历史项目字段为 undefined）
    projectMemo () {
      const p = this.currentDetail && this.currentDetail.instance && this.currentDetail.instance.projectId
      return (p && p.firstMaterialMemo) || {}
    },

    // 当前激活的节点（状态为 IN_PROGRESS 且 order 最小的）
    currentActiveNode () {
      if (!this.currentDetail || !this.currentDetail.nodes) return null
      const activeNodes = this.currentDetail.nodes.filter(n => n.status === 'IN_PROGRESS')
      if (activeNodes.length === 0) return null
      return activeNodes.sort((a, b) => a.order - b.order)[0]
    },

    // 过滤后的节点列表（根据开关控制是否显示已完成节点）
    filteredNodes () {
      if (!this.currentDetail?.nodes) return []
      if (this.showCompletedNodes) return this.currentDetail.nodes
      return this.currentDetail.nodes.filter(
        n => n.status !== 'COMPLETED' && n.status !== 'SKIPPED'
      )
    },

    // 筹建中详情地址
    detailAddress () {
      if (!this.currentDetail) return ''
      // 优先从 DATA_COLLECT 节点获取已填写的地址
      const dataCollectNode = (this.currentDetail.nodes || []).find(n => n.code === 'DATA_COLLECT')
      if (dataCollectNode?.data?.storeAddress) return dataCollectNode.data.storeAddress
      // 其次从 project/store 获取
      const storeMap = this.currentDetail.project?.storeMap || this.currentDetail.store?.storeMap
      if (storeMap?.address) return storeMap.address
      return this.currentDetail.store?.address || ''
    },

    // 资料收集节点数据
    dataCollectNode () {
      if (!this.currentDetail?.nodes) return null
      return this.currentDetail.nodes.find(n => n.code === 'DATA_COLLECT')
    },

    // 平台账号相关节点数据
    accountNodes () {
      if (!this.currentDetail?.nodes) return {}
      const notify = this.currentDetail.nodes.find(n => n.code === 'ACCOUNT_NOTIFY')
      const create = this.currentDetail.nodes.find(n => n.code === 'ACCOUNT_CREATE')
      const assign = this.currentDetail.nodes.find(n => n.code === 'ASSIGN_OPERATOR')
      return { notify, create, assign }
    },

    // 产品审核节点数据
    productReviewNode () {
      if (!this.currentDetail?.nodes) return null
      return this.currentDetail.nodes.find(n => n.code === 'PRODUCT_REVIEW')
    },

    // 财务门店映射最新编码（详情接口 res.data.scmCodes 整体替换，天然响应式）
    // MEITUAN_ORDER_REGISTER 只读弹窗编码展示的权威源（node.data 仅为提交时快照）
    scmCodeBaseline () {
      const c = this.currentDetail?.scmCodes
      return {
        meituanCode: String(c?.meituanCode || '').trim(),
        shihengCode: String(c?.shihengCode || '').trim()
      }
    }
  },
  created () {
    this.base = window.sessionStorage.getItem('base')
    this.loadInstanceDetail()
  },
  methods: {
    // ==================== 页面核心方法 ====================

    // 首批物料备忘截图地址（相对路径拼附件基地址）
    getMemoImg (img) {
      return this.base + img
    },

    // 统一内容判空（模板可用）
    hasMemo,

    // 加载流程实例详情（复用通用接口，与督导详情页同款调用）
    async loadInstanceDetail () {
      const instanceId = this.$route.params.instanceId
      if (!instanceId) {
        this.$message.error('缺少实例ID参数')
        return
      }

      this.pageLoading = true
      try {
        // 只读页面无日志展示，不请求 logs（省去 50 条日志查询）
        const { data: res } = await this.$http.get(`process/instances/${instanceId}`)
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        this.currentDetail = res.data
      } catch (error) {
        console.error('获取详情失败:', error)
        this.$message.error('获取筹建详情失败')
      } finally {
        this.pageLoading = false
      }
    },

    // 刷新详情数据
    async refreshDetail () {
      const instanceId = this.$route.params.instanceId || this.currentDetail?.instance?._id
      if (!instanceId) {
        return this.$message.error('找不到实例ID')
      }
      const loading = this.$loading({
        lock: true,
        text: '刷新中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        // 只读页面无日志展示，不请求 logs
        const { data: res } = await this.$http.get(`process/instances/${instanceId}`)
        if (res.meta.status === 200) {
          this.currentDetail = res.data
          this.$message.success('刷新成功')
        } else {
          this.$message.error(res.meta.msg)
        }
      } catch (error) {
        console.error('刷新详情失败:', error)
        this.$message.error('刷新失败')
      } finally {
        loading.close()
      }
    },

    // 返回筹建列表
    goBack () {
      this.$router.push('/yunying/choujian')
    },

    // ==================== 详情辅助方法 ====================

    // 从 assignees 数组中获取指定角色的名称
    getAssigneeName (assignees, role) {
      if (!assignees || !Array.isArray(assignees)) return '-'
      const person = assignees.find(a => a.role === role)
      return person?.employeeName || '-'
    },

    // 根据进度百分比返回颜色
    getProgressColor (percentage) {
      if (percentage >= 100) return '#67C23A'
      if (percentage >= 60) return '#409EFF'
      if (percentage >= 30) return '#E6A23C'
      return '#F56C6C'
    },

    // 获取阶段进度百分比
    getStagePercentage (stage) {
      if (!this.currentDetail || !this.currentDetail.nodes) return 0
      const stageNodes = this.currentDetail.nodes.filter(n => stage.nodeCodes.includes(n.code))
      if (stageNodes.length === 0) return 0
      const completed = stageNodes.filter(n => n.status === 'COMPLETED' || n.status === 'SKIPPED').length
      return Math.round((completed / stageNodes.length) * 100)
    },

    // 获取阶段样式类
    getStageClass (stage) {
      const percentage = this.getStagePercentage(stage)
      if (percentage >= 100) return 'stage-complete'
      if (percentage > 0) return 'stage-processing'
      return 'stage-pending'
    },

    // 获取阶段状态文字
    getStageStatusText (stage) {
      if (!this.currentDetail || !this.currentDetail.nodes) return '待开始'
      const stageNodes = this.currentDetail.nodes.filter(n => stage.nodeCodes.includes(n.code))
      if (stageNodes.length === 0) return '待开始'
      const completed = stageNodes.filter(n => n.status === 'COMPLETED' || n.status === 'SKIPPED').length
      if (completed === 0) return '待开始'
      if (completed === stageNodes.length) return `${completed}/${stageNodes.length} 完成`
      return `${completed}/${stageNodes.length} 完成`
    },

    // 获取阶段进度条颜色
    getStageColor (stage) {
      const percentage = this.getStagePercentage(stage)
      if (percentage >= 100) return '#67C23A'
      if (percentage > 0) return '#E42313'
      return '#E8E8E8'
    },

    // 计算门店开业天数
    getOpenedDays (completedAt) {
      if (!completedAt) return 0
      const completed = new Date(completedAt)
      const today = new Date()
      completed.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      return Math.floor((today - completed) / (86400000))
    },

    // ==================== 节点状态方法 ====================

    // 获取节点状态类型（用于 el-tag）
    getNodeStatusType (status) {
      const typeMap = {
        PENDING: 'info',
        IN_PROGRESS: 'danger',
        IN_MODIFYING: 'danger',
        COMPLETED: 'success',
        SKIPPED: 'warning',
        RETURNED: 'warning'
      }
      return typeMap[status] || 'info'
    },

    // 获取节点状态文字
    getNodeStatusText (node) {
      if (node.type === 'LOOP') {
        if (node.status === 'COMPLETED') return `完成 (${node.loopCount}次)`
        if (node.status === 'IN_PROGRESS') return `${node.loopCount}/${node.loopConfig?.minCount || 1}次`
      }
      const textMap = {
        PENDING: '待处理',
        IN_PROGRESS: '进行中',
        IN_MODIFYING: '修改中',
        COMPLETED: '已完成',
        SKIPPED: '已跳过',
        RETURNED: '已退回'
      }
      return textMap[node.status] || '未知'
    },

    // 判断节点是否处于执行中状态（只读视角仅用于展示"等待完成"文案）
    canExecuteNode (node) {
      if (node.status === 'IN_PROGRESS') return true
      if (node.status === 'IN_MODIFYING') return true
      if (node.type === 'LOOP' && (node.status === 'IN_PROGRESS')) return true
      if (node.type === 'BRANCH' && node.status === 'IN_PROGRESS') return true
      return false
    },

    // 节点详情只读展示取值：美团订货注册的编码字段以财务门店映射最新值为准
    // （project_scm_maps 为唯一权威源，node.data 仅为提交时快照，财务改码后不回填）
    getNodeDataDisplayValue (field) {
      if (this.currentDetailNode?.code === 'MEITUAN_ORDER_REGISTER' &&
        (field.fieldCode === 'meituanCode' || field.fieldCode === 'shihengCode')) {
        return this.scmCodeBaseline[field.fieldCode]
      }
      return this.currentDetailNode.data[field.fieldCode]
    },

    // 查看节点详情
    handleNodeDetail (node) {
      this.currentDetailNode = node
      this.nodeDetailDialogVisible = true
    },

    // 处理节点行点击
    handleNodeRowClick () {
      // 预留：可扩展节点详情查看
    },

    // 显示节点历史
    showNodeHistory () {
      this.$message.info('查看历史记录')
    }
  }
}
</script>

<style lang="less" scoped>
// 详情页容器
.detail-page-container {
  padding: 0 20px;
}

// ===== 新详情页样式 =====
// 首批物料备忘警示卡片（与督导详情页同款）
.detail-memo-card {
  background: #FDF6EC;
  border-left: 3px solid #E6A23C;
  border-radius: 4px;
  padding: 12px 14px;
  margin-top: 20px;
}
.detail-memo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.detail-memo-title {
  font-weight: bold;
  color: #B88230;
  font-size: 14px;

  i {
    margin-right: 4px;
  }
}
.detail-memo-source {
  font-size: 12px;
  color: #909399;
}
.detail-memo-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
}
.detail-memo-images {
  margin-top: 8px;
}

// 顶部项目信息区
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 20px;
  border-bottom: 1px solid #E8E8E8;
}

.detail-header-left {
  flex: 1;
}

.detail-project-name {
  font-size: 20px;
  font-weight: bold;
  color: #0D0D0D;
  margin-bottom: 8px;

  i {
    margin-right: 8px;
    color: #409eff;
  }
}

.detail-meta-row {
  display: flex;
  gap: 24px;
}

.detail-meta-item {
  font-size: 13px;
}

.detail-meta-label {
  color: #7A7A7A;
}

.detail-meta-value {
  color: #0D0D0D;
  font-weight: 500;
}

.detail-address {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 13px;

  i {
    color: #F56C6C;
    font-size: 16px;
    flex-shrink: 0;
  }
}

.detail-address-text {
  color: #606266;
  line-height: 1.4;
}

.detail-refresh-icon {
  cursor: pointer;
  color: #909399;
  transition: all 0.3s;
  margin-left: 8px;
  font-size: 14px;

  &:hover {
    color: #409EFF;
    transform: rotate(180deg);
  }
}

.detail-progress-card {
  width: 180px;
  padding: 16px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  text-align: center;
}

.detail-progress-label {
  font-size: 12px;
  color: #7A7A7A;
  margin-bottom: 8px;
}

.detail-progress-value {
  margin-bottom: 8px;
}

.detail-progress-number {
  font-size: 36px;
  font-weight: 600;
  color: #0D0D0D;
}

.detail-progress-unit {
  font-size: 18px;
  color: #7A7A7A;
}

.detail-progress-info {
  font-size: 12px;
  color: #B0B0B0;
  margin-top: 8px;
}

// 阶段进度区域
.detail-section {
  margin-bottom: 24px;
}

.detail-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.detail-section-title {
  font-size: 16px;
  font-weight: bold;
  color: #0D0D0D;
  padding: 10px 0;
  padding-top:15px;
}

.detail-section-toggle {
  display: flex;
  align-items: center;
}

.stage-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.stage-card {
  width: 20%;
  padding: 12px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.stage-name {
  font-size: 13px;
  font-weight: 500;
  color: #0D0D0D;
  margin-bottom: 4px;
}

.stage-status {
  font-size: 12px;
  margin-bottom: 8px;
}

.stage-pending {
  .stage-status { color: #B0B0B0; }
}

.stage-processing {
  .stage-status { color: #E42313; }
}

.stage-complete {
  .stage-status { color: #22C55E; }
}

// 底部信息区域
.detail-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid #E8E8E8;
  margin-top: 20px;
}

.detail-actions-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.detail-actions-label {
  font-size: 13px;
  color: #7A7A7A;
}

.detail-actions-node {
  font-size: 13px;
  font-weight: 500;
  color: #E42313;
}

.detail-actions-right {
  display: flex;
  gap: 12px;
}

// 文字辅助类
.text-muted {
  color: #B0B0B0;
}

.node-days-tag {
  font-size: 12px;
  color: #F56C6C;
  font-weight: normal;
  margin-left: 6px;
}

.text-waiting {
  color: #E6A23C;
  font-size: 12px;
}

// 节点详情弹窗
.node-detail-content {
  .node-detail-assignee {
    margin-top: 16px;
  }
  .node-detail-section-title {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    margin-bottom: 12px;
  }
  .node-detail-data {
    margin-top: 16px;
  }
  .node-detail-item {
    margin-bottom: 12px;
  }
  .node-detail-label {
    font-size: 13px;
    color: #909399;
    margin-bottom: 4px;
  }
  .node-detail-value {
    font-size: 14px;
    color: #303133;
  }
}
</style>
